// @file Vuex store Module for post connections
/* eslint-disable no-param-reassign */
import { JsonAPIResponse, JsonAPIResource } from '@padlet/arvo'
import { captureFetchException } from 'bits/error_tracker'
import PadletApi from 'surface/padlet_api'
import { PostConnection } from 'types'

export default {
  namespaced: true,
  state: {
    connectionsFetched: false,
    connectionEntities: {},
    pendingConnectionIds: [],
    postToConnectFromId: null,
    postToDisconnectFromId: null,
  },
  getters: {
    connections: (state, getters) => getters.connectionIds.map(id => state.connectionEntities[id]),
    connectionIds: state => {
      return Object.keys(state.connectionEntities)
    },
    xConnectionCancellation: (state): boolean => !!state.postToConnectFromId,
    xDisconnectionCancellation: (state): boolean => !!state.postToDisconnectFromId,
  },
  mutations: {
    FETCH_CONNECTIONS_SUCCESS(state, connections): void {
      if (connections) {
        const newConnectionEntities = {}
        connections.forEach(r => {
          newConnectionEntities[r.id] = r
        })
        state.connectionEntities = newConnectionEntities // Trigger state change once
        state.pendingConnectionIds = []
      }
      state.connectionsFetched = true
    },
    ADD_PENDING_CONNECTION(state, pendingConnection): void {
      const tempId = 'new_' + new Date().valueOf()
      state.connectionEntities = {
        ...state.connectionEntities,
        [tempId]: {
          ...pendingConnection,
          id: tempId,
        },
      }
      state.pendingConnectionIds.push(tempId)
    },
    ADD_CONNECTION(state, connection): void {
      const pendingConnectionIdRemoved = state.pendingConnectionIds.some(tempId => {
        if (
          state.connectionEntities[tempId].from_wish_id === connection.from_wish_id &&
          state.connectionEntities[tempId].to_wish_id === connection.to_wish_id &&
          state.connectionEntities[tempId].label === connection.label
        ) {
          const newConnectionEntities = { ...state.connectionEntities }
          // delete pending connection
          delete newConnectionEntities[tempId]
          // put new connection in place of pending connection
          state.connectionEntities = {
            ...newConnectionEntities,
            [connection.id]: connection,
          }
          state.pendingConnectionIds = state.pendingConnectionIds.filter(i => i !== tempId)
          return true
        }
        return false
      })
      if (!pendingConnectionIdRemoved) {
        state.connectionEntities = {
          ...state.connectionEntities,
          [connection.id]: connection,
        }
      }
    },
    REMOVE_CONNECTION(state, id): void {
      const newConnectionEntities = { ...state.connectionEntities }
      delete newConnectionEntities[id]
      state.connectionEntities = newConnectionEntities
    },
    REMOVE_CONNECTIONS(state, ids): void {
      const newConnectionEntities = { ...state.connectionEntities }
      ids.forEach(id => delete newConnectionEntities[id])
      state.connectionEntities = newConnectionEntities
    },
    START_CONNECTION(state, postId): void {
      state.postToConnectFromId = postId
    },
    START_DISCONNECTION(state, postId): void {
      state.postToDisconnectFromId = postId
    },
    CANCEL_CONNECTION(state): void {
      state.postToConnectFromId = null
    },
    CANCEL_DISCONNECTION(state): void {
      state.postToDisconnectFromId = null
    },
  },
  actions: {
    /* ------------
     * CRUD
     * ------------ */
    fetchConnections({ dispatch, rootGetters }): void {
      PadletApi.PostConnection.readAll({ wall: { id: rootGetters.wallId } })
        .then((fetchedData: JsonAPIResponse<PostConnection>) => {
          dispatch('fetchConnectionsSuccess', { fetchedData })
        })
        .catch(e => {
          captureFetchException(e, { source: 'fetchConnections' })
        })
    },
    fetchConnectionsSuccess({ commit }, { fetchedData }: { fetchedData: JsonAPIResponse<PostConnection> }): void {
      const connections = (fetchedData.data as JsonAPIResource<PostConnection>[]).map(
        connection => connection.attributes,
      )
      commit('FETCH_CONNECTIONS_SUCCESS', connections)
    },
    startConnection({ commit }, { postId }): void {
      commit('START_CONNECTION', postId)
    },
    startDisconnection({ commit }, { postId }): void {
      commit('START_DISCONNECTION', postId)
    },
    cancelConnection({ commit }): void {
      commit('CANCEL_CONNECTION')
    },
    cancelDisconnection({ commit }): void {
      commit('CANCEL_DISCONNECTION')
    },
    removeConnectionsForPost({ state, getters, commit }, { postId }): void {
      const relatedConnections = getters.connectionIds.filter(id => {
        return (
          state.connectionEntities[id].from_wish_id === postId || state.connectionEntities[id].to_wish_id === postId
        )
      })
      commit('REMOVE_CONNECTIONS', relatedConnections)
    },
    completeConnection({ commit, state, dispatch }, { postToConnectToId, label }): void {
      const newConnection = {
        from_wish_id: state.postToConnectFromId,
        to_wish_id: postToConnectToId,
        label: label,
      }
      commit('CANCEL_CONNECTION')
      commit('ADD_PENDING_CONNECTION', newConnection)
      PadletApi.PostConnection.create(newConnection).then((connectionData: JsonAPIResponse<PostConnection>) => {
        dispatch('completeConnectionSuccess', {
          connection: (connectionData.data as JsonAPIResource<PostConnection>).attributes,
        })
      })
    },
    completeConnectionSuccess({ commit }, { connection }): void {
      commit('ADD_CONNECTION', connection)
    },
    completeDisconnection({ commit, state }, { connectionId }): void {
      const existingConnection = state.connectionEntities[connectionId]
      commit('CANCEL_DISCONNECTION')
      if (existingConnection) {
        commit('REMOVE_CONNECTION', existingConnection.id)
        PadletApi.PostConnection.delete(existingConnection)
      }
    },

    /* ------------
     * REMOTE
     * ------------ */
    newConnectionRemote({ commit }, connection): void {
      commit('ADD_CONNECTION', connection)
    },
    deleteConnectionRemote({ commit }, connection): void {
      commit('REMOVE_CONNECTION', connection.id)
    },
  },
}
